// Track Types = bgMusic, ambience

const steps = {
  concrete: [
    'concrete-8',
    'concrete-9',
    'concrete-3',
    'concrete-4',
    'concrete-5',
    'concrete-6'
  ],
  grass: ['grass-b', 'grass-c', 'grass-d', 'grass-e', 'grass-f', 'grass-g']
};
const DEFAULT_TRACK_FADE_DURATION = 2200;
const SFX_AUDIO_KEYS = {
  click1: 'sfx_click1',
  click: 'sfx_click2',
  pointerover: 'sfx_rollover',
  door: 'sfx_door',
  key: 'sfx_key',
  unlock: 'sfx_unlock',
  switchImpact: 'sfx_switch_impact',
  gateCreak: 'sfx_gate_creak',
  gateRumble: 'sfx_gate_rumble',
  killPiece: 'sfx_kill_piece',
  newPartyMember: 'sfx_new_party_member',
  powerUp: 'sfx_power_up',
  battleResults: 'sfx_battle_results',
  chapterStart: 'sfx_chapter_1_start',
  amuletUsage: 'sfx_amulet_usage',
  forestSwitchOn: 'sfx_forest_switch_on',
  forestSwitchOff: 'sfx_forest_switch_off',
  seedShot: 'sfx_seed_shot',
  coins: 'sfx_coins',
  crash: 'sfx_crash',
  glassBreak: 'sfx_glass_break',
  queensFire: 'sfx_queens_fire',
  wolfHowl: 'sfx_wolf_howl',
  clothHeavy: 'sfx_cloth_heavy'
};

class SoundManager {
  constructor(game) {
    this.game = game;
    this.steps = steps;
    this.game.volumeMap = {
      ambience: 0.8,
      bgMusic: 0.2,
      sfx: 0.3
    };
    this.trackTweens = {};
    this.pendingTracks = {};
    this.game.sfx = {};
    Object.keys(SFX_AUDIO_KEYS).forEach(name => {
      this.game.sfx[name] = this.addSound(SFX_AUDIO_KEYS[name]);
    });
    steps.concrete.forEach(name => {
      this.game.sfx[name] = this.addSound(name);
    });
    steps.grass.forEach(name => {
      this.game.sfx[name] = this.addSound(name);
    });
    this.game.bgMusic =
      this.addSound('music_main') || this.addSound('music_battle');
    this.game.ambience = this.addSound('ambience_town_birds');
  }
  hasAudio(key) {
    return !!(
      key &&
      this.game.cache &&
      this.game.cache.audio &&
      this.game.cache.audio.exists(key)
    );
  }
  addSound(key) {
    return this.hasAudio(key) ? this.game.sound.add(key) : null;
  }
  resolveTrackName(trackName) {
    if (this.hasAudio(trackName)) {
      return trackName;
    }
    if (this.hasAudio('music_battle')) {
      return 'music_battle';
    }
    return null;
  }
  stopMusic() {
    this.cancelTrackTransition('bgMusic');
    this.cancelTrackTransition('ambience');
    this.game['bgMusic'] && this.game['bgMusic'].stop();
    this.game['ambience'] && this.game['ambience'].stop();
  }
  setVolumeOfType(volumeType, newVolume) {
    this.game.volumeMap[volumeType] = newVolume;
    if (this.game[volumeType]) {
      this.game[volumeType].volume = newVolume;
    }
  }
  playSfx(sfxName, customVolume) {
    let sound = this.game.sfx && this.game.sfx[sfxName];
    if (!sound) {
      sound = this.addSound(SFX_AUDIO_KEYS[sfxName] || sfxName);
      if (sound && this.game.sfx) {
        this.game.sfx[sfxName] = sound;
      }
    }
    if (!sound) {
      return false;
    }
    sound.play({
      volume: customVolume || this.game.volumeMap.sfx
    });
    return true;
  }
  getStepSoundsForGroundIndex(groundIndex) {
    const isGrass = groundIndex > 89 && groundIndex < 100;
    return isGrass ? this.steps.grass : this.steps.concrete;
  }
  playRandomStepForGroundIndex(groundIndex, volume = 0.15) {
    const groundType = this.getStepSoundsForGroundIndex(groundIndex);
    const availableTracks = groundType.filter(
      name => this.game.sfx && this.game.sfx[name]
    );
    if (!availableTracks.length) {
      return false;
    }
    const randTrack = Math.floor(Math.random() * availableTracks.length);
    return this.playSfx(availableTracks[randTrack], volume);
  }
  playRandomStepForTile(scene, tile, volume = 0.15) {
    if (!scene?.map || !tile) {
      return;
    }

    const groundTile = scene.map.getTileAt(
      tile.x,
      tile.y,
      true,
      'ground_layer'
    );
    if (!groundTile) {
      return;
    }

    this.playRandomStepForGroundIndex(groundTile.index, volume);
  }
  getTrackVolume(trackType, customVolume) {
    return customVolume ?? this.game.volumeMap[trackType] ?? 0;
  }
  cancelTrackTransition(trackType) {
    (this.trackTweens[trackType] || []).forEach(tween => tween.stop());
    this.trackTweens[trackType] = [];
    if (this.pendingTracks[trackType]) {
      this.pendingTracks[trackType].stop();
      this.pendingTracks[trackType] = null;
    }
    if (this.transitioningToTrackNameByType) {
      this.transitioningToTrackNameByType[trackType] = null;
    }
  }
  addTrackTween(trackType, tween) {
    if (!this.trackTweens[trackType]) {
      this.trackTweens[trackType] = [];
    }
    this.trackTweens[trackType].push(tween);
  }
  playTrack(trackName, trackType, scene, fadeDuration = 0, customVolume) {
    const resolvedTrackName = this.resolveTrackName(trackName);
    if (!resolvedTrackName) {
      return false;
    }
    this.cancelTrackTransition(trackType);
    if (!this.game[trackType]) {
      this.game[trackType] = this.addSound(resolvedTrackName);
    }
    this.game[trackType] && this.game[trackType].stop();
    this.game[trackType] = this.addSound(resolvedTrackName);
    if (!this.game[trackType]) {
      return false;
    }
    const targetVolume = this.getTrackVolume(trackType, customVolume);
    this.game[trackType].play({
      volume: scene && fadeDuration ? 0 : targetVolume,
      loop: true
    });
    if (scene && fadeDuration && targetVolume) {
      this.addTrackTween(
        trackType,
        scene.tweens.add({
          targets: this.game[trackType],
          volume: targetVolume,
          duration: fadeDuration
        })
      );
    }
    return true;
  }
  transitionToTrack = (
    scene,
    trackName = 'music_town',
    trackType,
    speed = 'fast',
    customVolume
  ) => {
    const resolvedTrackName = this.resolveTrackName(trackName);
    if (!resolvedTrackName) {
      return false;
    }
    this.transitioningToTrackNameByType =
      this.transitioningToTrackNameByType || {};
    if (!this.game[trackType]) {
      this.game[trackType] = this.addSound(resolvedTrackName);
    }
    if (!this.game[trackType] || !this.game[trackType].isPlaying) {
      this.playTrack(
        resolvedTrackName,
        trackType,
        scene,
        DEFAULT_TRACK_FADE_DURATION,
        customVolume
      );
      return;
    }
    if (
      this.game[trackType].key !== resolvedTrackName &&
      this.transitioningToTrackNameByType[trackType] !== resolvedTrackName
    ) {
      this.cancelTrackTransition(trackType);
      const track2 = this.addSound(resolvedTrackName);
      if (!track2) {
        return false;
      }
      this.pendingTracks[trackType] = track2;
      track2.setVolume(0);
      track2.play({ volume: 0, loop: true });
      const targetVolume = this.getTrackVolume(trackType, customVolume);
      //new track turning up
      this.addTrackTween(
        trackType,
        scene.tweens.add({
          targets: track2,
          volume: targetVolume,
          duration: DEFAULT_TRACK_FADE_DURATION
        })
      );
      //old track turning down
      this.transitioningToTrackNameByType[trackType] = resolvedTrackName;
      const previousTrack = this.game[trackType];
      this.addTrackTween(
        trackType,
        scene.tweens.add({
          targets: previousTrack,
          volume: 0,
          duration: DEFAULT_TRACK_FADE_DURATION,
          onComplete: () => {
            if (this.pendingTracks[trackType] !== track2) {
              return;
            }
            this.game[trackType] = track2;
            this.pendingTracks[trackType] = null;
            this.transitioningToTrackNameByType[trackType] = null;
            this.trackTweens[trackType] = [];
            previousTrack.stop();
          }
        })
      );
    }
    return true;
  };
}

export default SoundManager;
